
"use client";

import { useState, useEffect, useRef } from 'react';
import { useRouter } from 'next/navigation';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Loader2, LogIn, Fingerprint } from 'lucide-react';
import { SnafLogo } from '@/components/SnafLogo';
import { signInWithEmailAndPassword } from 'firebase/auth';
import { collection, query, where, getDocs, limit, doc, onSnapshot } from 'firebase/firestore';
import { auth, db } from '@/lib/firebase';
import { useToast } from '@/hooks/use-toast';

const versionId = process.env.NEXT_PUBLIC_VERSION_ID || 'N/A';

export default function LoginPage() {
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [companyName, setCompanyName] = useState('SNA FIN_O2');
  const router = useRouter();
  const { toast } = useToast();
  const longPressTimer = useRef<NodeJS.Timeout>();

   useEffect(() => {
    const settingsRef = doc(db, 'settings', 'app_details');
    const unsubscribe = onSnapshot(settingsRef, (docSnap) => {
        if (docSnap.exists()) {
            setCompanyName(docSnap.data().companyName || 'SNA FIN_O2');
        }
    });
    return () => unsubscribe();
  }, []);
  
  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!username || !password) {
        toast({
            variant: "destructive",
            title: "Missing fields",
            description: "Please enter both username and password.",
        });
        return;
    }
    
    setIsLoading(true);
    
    try {
      await signInWithEmailAndPassword(auth, username, password);
      router.push('/dashboard');
    } catch (error) {
        try {
            const q = query(
                collection(db, 'users'), 
                where('username', '==', username), 
                where('password', '==', password),
                where('role', '==', 'field-agent'),
                limit(1)
            );
            const querySnapshot = await getDocs(q);

            if (!querySnapshot.empty) {
                const agentDoc = querySnapshot.docs[0];
                localStorage.setItem('agentId', agentDoc.id); 
                router.push('/agent-dashboard');
            } else {
                 toast({
                    variant: "destructive",
                    title: "Login Failed",
                    description: "Invalid username or password. Please try again.",
                 });
            }
        } catch (firestoreError) {
             console.error("Firestore Auth Error:", firestoreError);
             toast({
                variant: "destructive",
                title: "Login Error",
                description: "An unexpected error occurred. Please try again.",
             });
        }
    } finally {
        setIsLoading(false);
    }
  };
  
  const handleFingerprintLogin = () => {
    toast({
        title: "Feature Not Available",
        description: "Fingerprint login will be implemented in a future update."
    });
  }

  const handleVersionLongPress = () => {
    const buildUrl = `https://console.cloud.google.com/cloud-build/builds;region=us-central1?project=e-01-20453857-41c1c`;
    window.open(buildUrl, '_blank');
  };

  const handleMouseDown = () => {
    longPressTimer.current = setTimeout(() => {
        handleVersionLongPress();
    }, 1000); // 1 second long press
  };

  const handleMouseUp = () => {
    if(longPressTimer.current) {
        clearTimeout(longPressTimer.current);
    }
  };


  return (
    <main className="flex min-h-screen w-full items-center justify-center bg-gradient-to-br from-yellow-400 via-amber-500 to-orange-500 p-4">
      <div className="grid w-full max-w-4xl grid-cols-1 overflow-hidden rounded-2xl shadow-2xl md:grid-cols-2">
        
        <div className="hidden items-center justify-center bg-amber-600/50 p-8 text-white md:flex">
          <div className="text-center">
             <div className="relative mx-auto mb-6 h-48 w-48 rounded-full p-4 bg-white/20 shadow-lg">
                <SnafLogo className="h-full w-full" />
            </div>
            <h1 className="text-4xl font-bold tracking-tight">SNA FIN_O2</h1>
            <p className="mt-2 text-lg opacity-80">Empowering Microfinance</p>
          </div>
        </div>

        <Card className="w-full border border-amber-300/20 bg-background/80 backdrop-blur-sm md:rounded-l-none">
          <CardHeader className="text-center">
            <div className="mb-4 flex justify-center md:hidden">
              <div className="relative rounded-full bg-gradient-to-r from-yellow-500 to-orange-500 p-2">
                <SnafLogo className="h-20 w-20" />
              </div>
            </div>
            <CardTitle className="text-2xl">Welcome Back!</CardTitle>
            <h2 className="text-3xl font-extrabold bg-gradient-to-r from-yellow-600 to-orange-500 bg-clip-text text-transparent drop-shadow-md py-2" style={{textShadow: '2px 2px 4px rgba(0,0,0,0.1)'}}>
              {companyName}
            </h2>
            <CardDescription>Enter your credentials to access your dashboard.</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              <form onSubmit={handleLogin} className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="username">Username / Email</Label>
                  <Input
                    id="username"
                    type="text"
                    placeholder="admin@gmail.com or agent_username"
                    value={username}
                    onChange={(e) => setUsername(e.target.value)}
                    required
                    disabled={isLoading}
                    className="bg-pink-100"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="password">Password</Label>
                  <Input
                    id="password"
                    type="password"
                    placeholder="********"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    required
                    disabled={isLoading}
                    className="bg-pink-100"
                  />
                </div>
                <Button type="submit" className="w-full bg-pink-600 hover:bg-pink-700 text-white" disabled={isLoading}>
                  {isLoading ? (
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  ) : (
                    <LogIn className="mr-2 h-4 w-4" />
                  )}
                  {isLoading ? 'Logging in...' : 'Login'}
                </Button>
              </form>

              <div className="relative">
                <div className="absolute inset-0 flex items-center">
                  <span className="w-full border-t" />
                </div>
                <div className="relative flex justify-center text-xs uppercase">
                  <span className="bg-background px-2 text-muted-foreground">
                    Or continue with
                  </span>
                </div>
              </div>

              <Button variant="outline" className="w-full" onClick={handleFingerprintLogin} disabled={isLoading}>
                <Fingerprint className="mr-2 h-4 w-4" />
                Sign in with Fingerprint
              </Button>
            </div>
            <div 
              className="mt-6 text-center text-xs text-muted-foreground"
            >
              <button 
                onMouseDown={handleMouseDown}
                onMouseUp={handleMouseUp}
                onTouchStart={handleMouseDown}
                onTouchEnd={handleMouseUp}
                className="cursor-pointer"
              >
                Current Version: {versionId}
              </button>
            </div>
          </CardContent>
        </Card>

      </div>
    </main>
  );
}
